import React from 'react';

import Infotip from 'App/common/Tooltip/Infotip/infotip';
import { Article, Measurement } from 'models/shipments';
import { useAppSelector } from 'store/store';

import { selectIsAuthed } from '../../../../store/customer';
import { useArticles } from '../../hooks/shipment.hooks';
import { DIMENSION_WARNING_TEST_ID, MEASUREMENTS_TEST_ID } from '../DetailsPanel.test-ids';

import styles from './SizeWeight.module.scss';

function createMeasurementsStr(size: Measurement[], weight?: Measurement | null) {
  // Get Units
  const sizeUnits = size.map(({ unit }) => unit?.toLowerCase()).filter((unit) => !!unit);
  const distinctUnits = new Set(sizeUnits);

  let sizeStr = '';
  if (distinctUnits.size > 1) {
    const dimensionsStr = size.map((dimension) => dimension.value + (dimension.unit?.toLowerCase() || ''));
    sizeStr = dimensionsStr.join(' x ');
  } else {
    const dimensionsStr = size.map((dimension) => dimension.value);
    sizeStr = dimensionsStr.join(' x ');
    sizeStr += sizeUnits.length ? sizeUnits[0] : '';
  }

  let weightStr = '';
  if (weight?.value) {
    weightStr = weight.value + (weight.unit?.toLowerCase() || '');
  }

  return sizeStr && weightStr ? `${sizeStr}, ${weightStr}` : sizeStr || weightStr;
}

function displayMeasurements(articles: Article[]) {
  let dimWarning: string | boolean | undefined;
  const measurementsArray = articles
    .map((value) => value?.measurements)
    .map((measurements) => {
      const { length, width, height, weight } = measurements || {};
      // Filter out empty size measurements
      const sizeMeasurements = [length, width, height].filter((dimension) => !!dimension?.value) as Measurement[];

      let measurementsText;
      measurementsText = createMeasurementsStr(sizeMeasurements, weight);
      if (!dimWarning) {
        dimWarning = measurementsText && !(sizeMeasurements.length === 3 && weight?.value);
      }
      measurementsText = measurementsText || 'Not available';

      return measurementsText;
    });

  const displaySizeWeight =
    measurementsArray.length === 1 ? (
      <span>{measurementsArray[0]}</span>
    ) : (
      measurementsArray.map((measurementLine, index) => (
        <span key={index}>
          Parcel {index + 1}: {measurementLine} <br />
        </span>
      ))
    );

  const dimWarningText = dimWarning && (
    <p className={styles.textUnavailable} data-testid={DIMENSION_WARNING_TEST_ID}>
      Some dimensions are unavailable
      <Infotip anchorClassName={styles.infotip}>
        <p>One or more dimensions have not been entered into our system.</p>
      </Infotip>
    </p>
  );

  return (
    <>
      {displaySizeWeight}
      {dimWarningText}
    </>
  );
}

const SizeWeight: React.FC = () => {
  const articles = useArticles();
  const authenticated = useAppSelector(selectIsAuthed);

  return (
    <div className={styles.textContent} data-testid={MEASUREMENTS_TEST_ID}>
      {authenticated ? displayMeasurements(articles) : <span>Can&apos;t be shown</span>}
    </div>
  );
};

export default SizeWeight;
